import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
const _excluded = ["id", "schemaId", "label", "placeholder", "emptyPlaceholder", "activePlaceholder", "limit", "maxSize", "fileTypes", "helpTip", "uiType", "override", "labelForExpand", "isDisabled", "isRequired", "mappedFields"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { createUseStyles } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import FieldControl from "./FieldControl";
import Section from "./types/Section";
import InputGroup from "./types/InputGroup";
import MultiComponent from "./types/MultiComponent";
import CheckboxGroup from "./types/CheckboxGroup";
import Checkbox from "./types/Checkbox";
import RadioGroup from "./types/RadioGroup";
import BooleanRadioGroup from "./types/BooleanRadioGroup";
import List from "./types/List";
import MultiList from "./types/MultiList";
import Message from "./types/Message";
import Static from "./types/Static";
import Text from "./types/Text";
import Textarea from "./types/Textarea";
import Integer from "./types/Integer";
import NumberType from "./types/Number";
import Date from "./types/Date";
import Time from "./types/Time";
import Datetime from "./types/Datetime";
import File from "./types/File";
import WidgetSize from "./types/WidgetSize";
import ColorPicker from "./types/ColorPicker";
import ImagePicker from "./types/ImagePicker";
import Slider from "./types/Slider";
import Code from "./types/Code";
import getDefaults from "./lib/getDefaults";
import styles from "./styles/index";
export { default as useForceForm } from "./lib/useForceForm";
export { default as validate, errorMessages } from "./lib/validate";
export { default as unflatList } from "./lib/unflatList";
export { default as flatList } from "./lib/flatList";
export { default as processFromServer } from "./lib/processFromServer";
export { default as getDefaults } from "./lib/getDefaults";
const useStyles = createUseStyles(styles, 'ForceForm');
const name = 'ForceForm';
const TYPES = {
  Section,
  ExpandableComponent: InputGroup,
  MultiComponent,
  Text,
  Textarea,
  Number: NumberType,
  Integer,
  Checkbox,
  CheckboxGroup,
  BooleanRadioGroup,
  RadioGroup,
  List,
  MultiList,
  Message,
  Date,
  Time,
  Datetime,
  File,
  Static,
  WidgetSize,
  ColorPicker,
  ImagePicker,
  Slider,
  Code
};
const IS_MULTI_INPUT = ['ExpandableComponent', 'CheckboxGroup', 'RadioGroup', 'BooleanRadioGroup', 'MultiComponent'];
const STOP_COMPOSITION = ['ExpandableComponent', 'CheckboxGroup', 'RadioGroup', 'BooleanRadioGroup'];
function getValues(values, value, id, type) {
  let newValues = _objectSpread(_objectSpread({}, values), {}, {
    [id]: value
  });
  if (IS_MULTI_INPUT.includes(type)) {
    newValues = _objectSpread(_objectSpread({}, values), value);
  }
  return newValues;
}
function touchField(state, id, value, type) {
  if (IS_MULTI_INPUT.includes(type)) {
    Object.keys(value).forEach(key => {
      state[key] = _objectSpread({
        isTouched: state.originalValues[key] !== value[key]
      }, state[key]);
    });
  } else {
    state[id] = _objectSpread({
      isTouched: state.originalValues[id] !== value
    }, state[id]);
  }
}
function ForceForm(_ref) {
  let {
    className,
    schema,
    onSubmit,
    onChange,
    onState,
    onBlur,
    values = {},
    errors = {},
    mode,
    modifyFieldProps,
    isBulk,
    onBulkCancel,
    onBulkEdit,
    isWeb
  } = _ref;
  const internalState = useRef({
    originalValues: values
  });
  const defaults = useRef(getDefaults(schema));
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const requireds = useRef({});
  const isFirst = useRef(true);
  const classNames = classnames(classes.root, className, schema.layout ? `fs-form__${schema.layout.id}` : null);
  useEffect(() => {
    defaults.current = getDefaults(schema);
  }, [schema]);
  const onChangeField = useCallback((value, id, type, mappedFields) => {
    let newValues = values;
    if (mappedFields !== null && mappedFields !== void 0 && mappedFields.length && type === 'List') {
      var _value;
      const mappedValues = value ? (_value = value) === null || _value === void 0 ? void 0 : _value.data : mappedFields.reduce((obj, key) => {
        obj[key] = null;
        return obj;
      }, {});
      newValues = mappedFields !== null && mappedFields !== void 0 && mappedFields.length && type === 'List' ? _objectSpread(_objectSpread({}, values), mappedValues) : _objectSpread({}, values);
    } else if (type === 'Integer' || type === 'Number') {
      if (value) value = Number(value);
    } else if (type === 'Datetime') {
      var _value2;
      if (value) value = ((_value2 = value) === null || _value2 === void 0 ? void 0 : _value2.toISOString()) || value;
    }
    touchField(internalState.current, id, value, type);
    onState && onState(internalState.current);
    onChange && onChange(getValues(newValues, value, id, type), id, value);
  }, [onChange, onState, values]);
  const onBlurField = useCallback((value, id, type, mappedFields) => {
    let newValues = values;
    const finalValue = value === undefined || value === '' || value === null || Array.isArray(value) && !value.length ? defaults.current[id] || value : value;
    if (mappedFields !== null && mappedFields !== void 0 && mappedFields.length && type === 'List') {
      const mappedValues = value ? value === null || value === void 0 ? void 0 : value.data : mappedFields.reduce((obj, key) => {
        obj[key] = null;
        return obj;
      }, {});
      newValues = mappedFields !== null && mappedFields !== void 0 && mappedFields.length && type === 'List' ? _objectSpread(_objectSpread({}, values), mappedValues) : _objectSpread({}, values);
    }
    touchField(internalState.current, id, value);
    onState && onState(internalState.current);
    onBlur && onBlur(getValues(newValues, finalValue, id, type), id, finalValue);
  }, [onBlur, onState, values]);
  const onEnterField = useCallback((value, id, type) => {
    touchField(internalState.current, id, value);
    onState && onState(internalState.current);
    onSubmit && onSubmit(getValues(values, value, id, type), id, value);
  }, [onState, onSubmit, values]);
  const generateFormTree = useCallback((layout, idx) => {
    var _schema$properties;
    if (!layout) return null;
    const {
        id,
        schemaId,
        label,
        placeholder,
        emptyPlaceholder,
        activePlaceholder,
        limit,
        maxSize,
        fileTypes,
        helpTip,
        uiType,
        override = {},
        labelForExpand,
        isDisabled,
        isRequired,
        mappedFields
      } = layout,
      props = _objectWithoutProperties(layout, _excluded);
    const componentId = layout.schemaId || layout.id;
    const properties = ((_schema$properties = schema.properties) === null || _schema$properties === void 0 ? void 0 : _schema$properties[componentId]) || {};
    let composition = layout.composition;
    let type = uiType;
    if (type) type = type[0].toUpperCase() + type.slice(1);

    // First iteration
    if (idx === undefined) {
      requireds.current = schema.required ? schema.required.reduce((obj, item) => {
        obj[item] = true;
        return obj;
      }, {}) : {};
    }
    if (mappedFields !== null && mappedFields !== void 0 && mappedFields.length && isRequired) {
      requireds.current = _objectSpread(_objectSpread({}, requireds.current), {}, {
        [id]: true
      });
    }
    let fieldProps = _objectSpread({
      Field: TYPES[type],
      key: componentId || `${type}-${idx}`,
      id: componentId,
      type,
      label: label ? t(label) : '',
      rawLabel: label,
      placeholder: placeholder ? t(placeholder) : '',
      emptyPlaceholder: emptyPlaceholder ? t(emptyPlaceholder) : '',
      rawPlaceholder: placeholder,
      activePlaceholder: activePlaceholder ? t(activePlaceholder) : '',
      limit,
      maxSize,
      fileTypes,
      isFullWidth: true,
      isRequired: requireds.current[componentId],
      isReadOnly: properties.readOnly || isDisabled,
      labelForExpand: labelForExpand,
      schema,
      mappedFields
    }, props);

    // Overriding props by mode: ["create", "update"]
    if (mode && override && override[mode]) {
      fieldProps = _objectSpread(_objectSpread({}, fieldProps), override[mode] || {});
    }
    const isMultiInput = IS_MULTI_INPUT.includes(type);
    const hasComposition = !STOP_COMPOSITION.includes(type);
    const isInput = componentId && idx >= 0 && type || isMultiInput;
    if (isInput) {
      let isSomeChildRequired = false;
      fieldProps.name = componentId;
      fieldProps.hint = helpTip ? t(helpTip) : '';
      fieldProps.className = `fs-form__field__${componentId || idx}`;
      fieldProps.classes = {
        input: `fs-form__field__${componentId}--input`,
        clear: `fs-form__field__${componentId}--clear`
      };
      if (isMultiInput && composition) {
        fieldProps.inputs = composition.map(item => {
          var _schema$properties2, _schema$properties2$i, _item$hint, _item$hint2;
          const itemId = item.schemaId || item.id;
          if (requireds.current[itemId]) isSomeChildRequired = true;
          return {
            key: itemId,
            label: t(item.label),
            rawLabel: item.label,
            name: itemId,
            placeholder: item.placeholder ? t(item.placeholder) : '',
            emptyPlaceholder: item.emptyPlaceholder ? t(item.emptyPlaceholder) : '',
            rawPlaceholder: item.placeholder,
            isRequired: requireds.current[itemId],
            isReadOnly: ((_schema$properties2 = schema.properties) === null || _schema$properties2 === void 0 ? void 0 : (_schema$properties2$i = _schema$properties2[itemId]) === null || _schema$properties2$i === void 0 ? void 0 : _schema$properties2$i.readOnly) || item.isDisabled,
            isFullWidth: true,
            labelForExpand: labelForExpand,
            isHidden: item.isHidden || item.isHiddenForWeb && isWeb,
            hint: item.helpTip,
            info: (item === null || item === void 0 ? void 0 : item.hint) && {
              title: t((_item$hint = item.hint) === null || _item$hint === void 0 ? void 0 : _item$hint.title),
              body: t((_item$hint2 = item.hint) === null || _item$hint2 === void 0 ? void 0 : _item$hint2.body)
            }
          };
        });
        composition = hasComposition ? composition : null;
      }
      fieldProps.isRequired = isSomeChildRequired || requireds.current[componentId];
    }
    const children = composition ? composition.map(generateFormTree) : null;
    if (!type) return children;

    // Middleware function for props customization
    fieldProps = modifyFieldProps ? modifyFieldProps(fieldProps, schema) : fieldProps;
    return {
      Node: FieldControl,
      props: fieldProps,
      children
    };
  }, [isWeb, mode, modifyFieldProps, schema, t]);
  const generateForm = useCallback(formTree => {
    if (!formTree) return null;
    if (!Array.isArray(formTree)) formTree = [formTree];
    return formTree.map((node, index) => {
      var _node$props, _node$props$compositi, _node$props$compositi2, _node$props2, _node$props2$composit, _node$props2$composit2, _node$props$mappedFie;
      const id = node.props.id || ((_node$props = node.props) === null || _node$props === void 0 ? void 0 : (_node$props$compositi = _node$props.composition) === null || _node$props$compositi === void 0 ? void 0 : (_node$props$compositi2 = _node$props$compositi[0]) === null || _node$props$compositi2 === void 0 ? void 0 : _node$props$compositi2.id) || ((_node$props2 = node.props) === null || _node$props2 === void 0 ? void 0 : (_node$props2$composit = _node$props2.composition) === null || _node$props2$composit === void 0 ? void 0 : (_node$props2$composit2 = _node$props2$composit[0]) === null || _node$props2$composit2 === void 0 ? void 0 : _node$props2$composit2.schemaId);
      const error = errors[id] ? t(errors[id].message, errors[id].params) || errors[id].message : undefined;
      let value = values === null || values === void 0 ? void 0 : values[id];
      if ((_node$props$mappedFie = node.props.mappedFields) !== null && _node$props$mappedFie !== void 0 && _node$props$mappedFie.length && node.props.type === 'List') {
        node.props.options = node.props.options.map(el => _objectSpread(_objectSpread({}, el), {}, {
          label: t(el.label)
        }));
        value = node.props.options.find(el => Object.keys(el.data).reduce((acc, key) => {
          var _el$data;
          return acc && ((_el$data = el.data) === null || _el$data === void 0 ? void 0 : _el$data[key]) === (values === null || values === void 0 ? void 0 : values[key]);
        }, true));
        values[id] = value;
      }
      const props = _objectSpread(_objectSpread({}, node.props), {}, {
        error,
        value,
        onEnter: onEnterField,
        onChange: onChangeField,
        onBlur: onBlurField,
        autoFocus: isFirst.current && node.props.type !== 'Section' && !value,
        isBulk,
        bulkEditLabel: t('placeholder_click_to_edit'),
        bulkCancelLabel: t('action_cancel'),
        onBulkEdit,
        onBulkCancel,
        key: id
      });
      if (isFirst.current && props.type !== 'Section' && !value) isFirst.current = false;
      if (node.props.inputs) {
        var _props$inputs;
        props.inputs = (_props$inputs = props.inputs) === null || _props$inputs === void 0 ? void 0 : _props$inputs.filter(el => !el.isHidden);
      }
      if (node.props.isHidden) return null;
      if (IS_MULTI_INPUT.includes(node.props.type)) props.value = values;
      return /*#__PURE__*/React.createElement(node.Node, _extends({}, props, {
        key: id || index
      }), node.children ? node.children.map(generateForm) : null);
    });
  }, [errors, t, values, onEnterField, onChangeField, onBlurField, isBulk, onBulkEdit, onBulkCancel]);
  const formTree = useMemo(() => {
    let form = generateFormTree(schema.layout);
    return form;
  }, [generateFormTree, schema.layout]);
  const form = useMemo(() => generateForm(formTree), [formTree, generateForm]);
  return /*#__PURE__*/React.createElement("form", {
    className: classNames,
    autoComplete: "off"
  }, form);
}
process.env.NODE_ENV !== "production" ? ForceForm.propTypes = {
  className: PropTypes.string,
  values: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  schema: PropTypes.object.isRequired,
  modifyFieldProps: PropTypes.func,
  mode: PropTypes.string
} : void 0;
const component = /*#__PURE__*/React.memo(ForceForm);
component.displayName = name;
export default component;